import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { JobForMapsFilter, JobListCardItem, TeamView } from 'containers/dispatch/dispatch.model';
import { ViewType } from 'containers/dispatch/constants';
import { RootState } from 'store/reducers';
import { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import { AmountFilterOperation } from 'constants/common';
import { SearchItemType } from 'types/baseTypes';
import { jobSearchTypes } from 'containers/jobs/constant';

interface DispatchState {
  jobDetails: JobListCardItem;
  selectedJobId: string | null;
  selectedJobFromList: JobListCardItem | null;
  isLoading: boolean;
  error: string | null;
  selectedMapView: ViewType;
  isJobListCollapsed: boolean;
  currentActiveView: TeamView | null;
  currentViewFilters: Record<keyof JobForMapsFilter, string[] | string | number | AmountFilter | boolean | null>;
  mapEventData: any;
  viewFiltersMap: Record<
    string,
    Record<keyof JobForMapsFilter, string[] | string | number | AmountFilter | boolean | null>
  >;
  searchText: string;
  searchType: SearchItemType;
}

export const currentViewFiltersInitialState: Record<
  keyof JobForMapsFilter,
  string[] | string | number | AmountFilter | boolean | null
> = {
  status: [],
  assignees: [],
  customerIds: [],
  leadSource: [],
  createdBy: [],
  invoiceStatus: [],
  estimateStatus: [],
  amount: {
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  },
  scheduledFrom: null,
  scheduledTo: null,
  externalCompany: [],
  zipCode: null,
  radius: null,
  isToday: false,
  schedulePreset: null,
  tags: '',
  isUnscheduled: false,
  isUnassigned: false,
  serviceArea: [],
  createdFrom: null,
  createdTo: null,
  noTags: false
};

const initialState: DispatchState = {
  jobDetails: null,
  selectedJobId: null,
  isLoading: false,
  error: null,
  selectedMapView: ViewType.STATUS,
  isJobListCollapsed: false,
  currentActiveView: null,
  currentViewFilters: {
    status: [],
    assignees: [],
    customerIds: [],
    leadSource: [],
    createdBy: [],
    invoiceStatus: [],
    estimateStatus: [],
    amount: {
      filterOperation: AmountFilterOperation.ALL,
      minAmount: null,
      maxAmount: null
    },
    scheduledFrom: null,
    scheduledTo: null,
    zipCode: null,
    radius: null,
    externalCompany: [],
    isToday: false,
    schedulePreset: null,
    tags: '',
    isUnscheduled: false,
    isUnassigned: false,
    serviceArea: [],
    createdFrom: null,
    createdTo: null,
    noTags: false
  },
  mapEventData: null,
  viewFiltersMap: {},
  selectedJobFromList: null,
  searchText: '',
  searchType: jobSearchTypes[0]
};

const dispatchSlice = createSlice({
  name: 'dispatch',
  initialState,
  reducers: {
    setJobDetails: (state, action: PayloadAction<JobListCardItem>) => {
      state.jobDetails = action.payload;
    },
    setSelectedJobInList: (state, action: PayloadAction<JobListCardItem | null>) => {
      state.selectedJobFromList = action.payload;
    },
    setSelectedJobId: (state, action: PayloadAction<string | null>) => {
      state.selectedJobId = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSelectedMapViewByType: (state, action: PayloadAction<ViewType>) => {
      state.selectedMapView = action.payload;
    },
    setIsJobListCollapsed: (state, action: PayloadAction<boolean>) => {
      state.isJobListCollapsed = action.payload;
    },
    setCurrentActiveView: (state, action: PayloadAction<TeamView | null>) => {
      state.currentActiveView = action.payload;
    },
    setCurrentViewFilters: (
      state,
      action: PayloadAction<Record<keyof JobForMapsFilter, string[] | string | number | AmountFilter | boolean | null>>
    ) => {
      state.currentViewFilters = action.payload;
      if (state.currentActiveView) {
        state.viewFiltersMap[state.currentActiveView.id.toString()] = action.payload;
      }
    },
    setViewSpecificFilters: (
      state,
      action: PayloadAction<{
        viewId: string;
        filters: Record<keyof JobForMapsFilter, string[] | string | number | AmountFilter | boolean | null>;
      }>
    ) => {
      const { viewId, filters } = action.payload;
      state.viewFiltersMap[viewId] = filters;
      if (state.currentActiveView && state.currentActiveView.id.toString() === viewId) {
        state.currentViewFilters = filters;
      }
    },
    loadViewFilters: (state, action: PayloadAction<string>) => {
      const viewId = action.payload;
      const viewFilters = state.viewFiltersMap[viewId];
      if (viewFilters) {
        state.currentViewFilters = viewFilters;
      } else {
        state.currentViewFilters = currentViewFiltersInitialState;
      }
    },
    clearAllViewFilters: state => {
      state.viewFiltersMap = {};
      state.currentViewFilters = currentViewFiltersInitialState;
    },
    addMapEventData: (state, action: PayloadAction<any>) => {
      state.mapEventData = action.payload;
    },
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    setSearchType: (state, action: PayloadAction<SearchItemType>) => {
      state.searchType = action.payload;
    }
  }
});

export const {
  setJobDetails,
  setSelectedJobId,
  setLoading,
  setError,
  setSelectedMapViewByType,
  setIsJobListCollapsed,
  setCurrentActiveView,
  setCurrentViewFilters,
  setViewSpecificFilters,
  loadViewFilters,
  clearAllViewFilters,
  addMapEventData,
  setSelectedJobInList,
  setSearchText,
  setSearchType
} = dispatchSlice.actions;

export const selectJobDetails = (state: RootState) => state.rootReducer.dispatch.jobDetails;
export const selectSelectedJobId = (state: RootState) => state.rootReducer.dispatch.selectedJobId;
export const selectIsLoading = (state: RootState) => state.rootReducer.dispatch.isLoading;
export const selectError = (state: RootState) => state.rootReducer.dispatch.error;
export const selectSelectedMapView = (state: RootState) => state.rootReducer.dispatch.selectedMapView;
export const selectIsJobListCollapsed = (state: RootState) => state.rootReducer.dispatch.isJobListCollapsed;
export const selectCurrentActiveView = (state: RootState) => state.rootReducer.dispatch.currentActiveView;
export const selectCurrentViewFilters = (state: RootState) => state.rootReducer.dispatch.currentViewFilters;
export const selectMapEventData = (state: RootState) => state.rootReducer.dispatch.mapEventData;
export const selectSelectedJob = (state: RootState) => state.rootReducer.dispatch.jobDetails;
export const selectViewFiltersMap = (state: RootState) => state.rootReducer.dispatch.viewFiltersMap;
export const selectViewSpecificFilters = (viewId: string) => (state: RootState) =>
  state.rootReducer.dispatch.viewFiltersMap[viewId] || null;
export const selectSelectedJobFromList = (state: RootState) => state.rootReducer.dispatch.selectedJobFromList;
export const selectSearchText = (state: RootState) => state.rootReducer.dispatch.searchText;
export const selectSearchType = (state: RootState) => state.rootReducer.dispatch.searchType;

export default dispatchSlice.reducer;
